import Banner from "../layout/Banner";
import { StickyNavbar } from "../layout/StickyNavBar";
import React, { useEffect } from "react";
import Footer from "./Footer";

const TESTIMONIALS = [
  {
    name: "Samson Amare",
    quote:
      "I could see the fee and rate before I paid, so there were no surprises for my family.",
    location: "Toronto, ON, Canada",
  },
  {
    name: "Wondwesson Tesfaye",
    quote:
      "Signing up was quick, and I followed my transfer all the way until it arrived.",
    location: "Calgary, AB, Canada",
  },
  {
    name: "Abraham Belay",
    quote:
      "I had a question about verification and the support team helped me right away.",
    location: "Ottawa, ON, Canada",
  },
  {
    name: "Tsegay Gebremedhin",
    quote:
      "It was simple to see how much my family would receive before I sent the money.",
    location: "Winnipeg, MB, Canada",
  },
  {
    name: "Timnit Tesfay",
    quote:
      "The updates made it easy to know where the transfer was at every step.",
    location: "Edmonton, AB, Canada",
  },
  {
    name: "Sharon Desta",
    quote:
      "I got help with my account quickly and felt supported through the process.",
    location: "Vancouver, BC, Canada",
  },
];

const Home = () => {
  useEffect(() => {
    localStorage.removeItem("token");
  }, []);

  useEffect(() => {
    const page = document.querySelector(".landing-page");
    if (!page || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute("data-revealed", "true");
            currentObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    page.classList.add("reveal-enabled");
    page.querySelectorAll("[data-reveal]").forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
      page.classList.remove("reveal-enabled");
    };
  }, []);

  return (
    <div className="landing-page min-h-screen bg-[#f7f3ed] text-[#302521]">
      <div className="fixed inset-x-0 top-0 z-50">
        <StickyNavbar />
      </div>
      <div className="h-[60px]" aria-hidden="true" />

      <main>
        <Banner />

        <section id="how-it-works" className="scroll-mt-20 border-b border-[#e8dfd2] bg-[#f3eee7] px-4 py-16 sm:px-6 sm:py-20">
          <div data-reveal="up" className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              See every step before you pay
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#766b64] sm:text-base">
              You always know the rate, the fee and what your recipient gets.
            </p>
          </div>

          <div className="mx-auto mt-10 grid max-w-[820px] items-center gap-8 md:grid-cols-[1fr_1fr] md:gap-12">
            <ol className="space-y-5">
              <li data-reveal="left" data-reveal-delay="1" className="flex gap-3">
                <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e6ddd2] bg-[#fffdf9] text-[#882699] after:absolute after:left-1/2 after:top-full after:h-5 after:border-l after:border-dashed after:border-[#cdb8ce]">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <rect x="6" y="3" width="12" height="18" rx="2" />
                    <path strokeLinecap="round" d="M9 7h6M9 11h2M13 11h2M9 15h2M13 15h2" />
                  </svg>
                </span>
                <div className="pt-0.5">
                  <h3 className="text-sm font-bold">Choose an amount</h3>
                  <p className="mt-1 text-xs leading-5 text-[#766b64] sm:text-[13px]">
                    Pick the country and enter what you want to send.
                  </p>
                </div>
              </li>
              <li data-reveal="left" data-reveal-delay="2" className="flex gap-3">
                <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e6ddd2] bg-[#fffdf9] text-[#882699] after:absolute after:left-1/2 after:top-full after:h-5 after:border-l after:border-dashed after:border-[#cdb8ce]">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h10a2 2 0 0 1 2 2v14l-7-4-7 4V6a2 2 0 0 1 2-2Z" />
                    <path strokeLinecap="round" d="M9 8h6M9 11h6" />
                  </svg>
                </span>
                <div className="pt-0.5">
                  <h3 className="text-sm font-bold">Review the rate and fee</h3>
                  <p className="mt-1 text-xs leading-5 text-[#766b64] sm:text-[13px]">
                    Check the total and your recipient before confirming.
                  </p>
                </div>
              </li>
              <li data-reveal="left" data-reveal-delay="3" className="flex gap-3">
                <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e6ddd2] bg-[#fffdf9] text-[#882699] after:absolute after:left-1/2 after:top-full after:h-5 after:border-l after:border-dashed after:border-[#cdb8ce]">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <rect x="5" y="10" width="14" height="11" rx="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10V7a4 4 0 1 1 8 0v3" />
                  </svg>
                </span>
                <div className="pt-0.5">
                  <h3 className="text-sm font-bold">Pay securely</h3>
                  <p className="mt-1 text-xs leading-5 text-[#766b64] sm:text-[13px]">
                    Complete payment through the available checkout.
                  </p>
                </div>
              </li>
              <li data-reveal="left" data-reveal-delay="4" className="flex gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e6ddd2] bg-[#fffdf9] text-[#882699]">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m3 11 9-8 9 8M5 10v10h14V10M9 20v-6h6v6" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="m9 11 2 2 4-4" />
                  </svg>
                </span>
                <div className="pt-0.5">
                  <h3 className="text-sm font-bold">Follow it to arrival</h3>
                  <p className="mt-1 text-xs leading-5 text-[#766b64] sm:text-[13px]">
                    Track your transfer until your recipient has the money.
                  </p>
                </div>
              </li>
            </ol>

            <article data-reveal="right" className="rounded-[1.35rem] border border-[#e6ddd2] bg-[#fffdf9] p-5 shadow-[0_18px_45px_rgba(66,48,34,0.06)] sm:p-6">
              <h3 className="text-sm font-bold">Where your money goes</h3>
              <p className="mt-1 text-xs text-[#857970]">
                Example: sending 100 CAD to Eritrea
              </p>
              <div className="mt-5 flex h-3 overflow-hidden rounded-full bg-[#eee8df]">
                <span className="w-[94%] rounded-full bg-[#882699]" />
                <span className="w-[6%] rounded-full bg-[#c9a45b]" />
              </div>
              <div className="mt-4 space-y-3 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-[#766b64]">
                    <span className="h-2 w-2 rounded-full bg-[#882699]" />
                    Sent to your recipient
                  </span>
                  <strong>100.00 CAD</strong>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-[#766b64]">
                    <span className="h-2 w-2 rounded-full bg-[#c9a45b]" />
                    Our fee
                  </span>
                  <strong>5.00 CAD</strong>
                </div>
              </div>
              <div className="my-4 border-t border-[#e9e1d8]" />
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#766b64]">You pay</span>
                <strong className="text-base">105.00 CAD</strong>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                <span className="text-[#766b64]">Recipient gets</span>
                <span className="flex items-center gap-2">
                  <strong>1,050 NKF</strong>
                  <span className="rounded-full bg-[#e2f1e6] px-2 py-1 text-[10px] font-semibold text-[#27613b]">
                    at 10.50
                  </span>
                </span>
              </div>
            </article>
          </div>
        </section>

        <section id="why-adulis" className="scroll-mt-20 border-b border-[#e8dfd2] bg-[#faf7f2] px-4 py-16 sm:px-6 sm:py-20">
          <div data-reveal="up" className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Built on clarity
            </h2>
            <p className="mt-3 text-sm leading-6 text-[#766b64] sm:text-base">
              Sending money is personal. We keep it simple and dependable.
            </p>
          </div>

          <div className="mx-auto mt-10 grid max-w-[850px] gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <article data-reveal="up" data-reveal-delay="1" className="overflow-hidden rounded-2xl border border-[#e6ddd2] bg-[#fffdf9]">
              <div className="flex h-[112px] items-center justify-center bg-[#f0e7ec]">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#eaddec]">
                  <svg className="h-12 w-12 text-[#882699]" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                    <path d="M24 5 39 11v11c0 9-6 16-15 21C15 38 9 31 9 22V11l15-6Z" fill="currentColor" />
                    <path d="m17 23 5 5 10-11" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="7" cy="13" r="2.5" fill="#c9a45b" />
                    <circle cx="42" cy="34" r="2.5" fill="#a847b7" />
                  </svg>
                </span>
              </div>
              <div className="min-h-[128px] p-4">
                <h3 className="text-sm font-bold">Safe and compliant</h3>
                <p className="mt-2 text-xs leading-[1.55] text-[#766b64]">
                  We follow money transfer regulations and verify identities to protect you and your recipient.
                </p>
              </div>
            </article>

            <article data-reveal="up" data-reveal-delay="2" className="overflow-hidden rounded-2xl border border-[#e6ddd2] bg-[#fffdf9]">
              <div className="flex h-[112px] items-center justify-center bg-[#e9eee8]">
                <svg className="h-[76px] w-[100px] text-[#882699]" viewBox="0 0 100 76" fill="none" aria-hidden="true">
                  <circle cx="47" cy="37" r="29" stroke="currentColor" strokeWidth="4" />
                  <path d="M47 20v18l14 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="73" cy="62" r="11" fill="#319a5b" />
                  <path d="m68 62 3.5 3.5 6-7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="91" cy="12" r="3" fill="#c9a45b" />
                  <path d="M14 10a34 34 0 0 1 9-6" stroke="#a847b7" strokeWidth="2" strokeLinecap="round" strokeDasharray="1 5" />
                </svg>
              </div>
              <div className="min-h-[128px] p-4">
                <h3 className="text-sm font-bold">Dependable</h3>
                <p className="mt-2 text-xs leading-[1.55] text-[#766b64]">
                  The same clear process every time you send, from payment to delivery.
                </p>
              </div>
            </article>

            <article data-reveal="up" data-reveal-delay="3" className="overflow-hidden rounded-2xl border border-[#e6ddd2] bg-[#fffdf9]">
              <div className="flex h-[112px] items-center justify-center bg-[#f0e9dc]">
                <svg className="h-[82px] w-[92px]" viewBox="0 0 92 82" fill="none" aria-hidden="true">
                  <rect x="18" y="4" width="56" height="74" rx="9" fill="#403833" />
                  <rect x="28" y="16" width="24" height="5" rx="2.5" fill="#71635a" />
                  <rect x="57" y="16" width="10" height="5" rx="2.5" fill="#71635a" />
                  <rect x="28" y="30" width="39" height="5" rx="2.5" fill="#71635a" />
                  <rect x="28" y="44" width="39" height="5" rx="2.5" fill="#71635a" />
                  <rect x="28" y="59" width="23" height="7" rx="3.5" fill="#882699" />
                  <rect x="57" y="59" width="15" height="7" rx="3.5" fill="#a847b7" />
                  <circle cx="83" cy="13" r="7" fill="#c9a45b" />
                  <circle cx="8" cy="62" r="4" fill="#c9a45b" />
                </svg>
              </div>
              <div className="min-h-[128px] p-4">
                <h3 className="text-sm font-bold">Fully transparent</h3>
                <p className="mt-2 text-xs leading-[1.55] text-[#766b64]">
                  Fees and exchange rates are listed in full before you pay. Nothing is added later.
                </p>
              </div>
            </article>

            <article data-reveal="up" data-reveal-delay="4" className="overflow-hidden rounded-2xl border border-[#e6ddd2] bg-[#fffdf9]">
              <div className="flex h-[112px] items-center justify-center bg-[#f0e7ec]">
                <svg className="h-[82px] w-[100px] text-[#75675f]" viewBox="0 0 100 82" fill="none" aria-hidden="true">
                  <circle cx="50" cy="41" r="34" stroke="currentColor" strokeWidth="1.5" />
                  <ellipse cx="50" cy="41" rx="14" ry="34" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M17 41h66M23 24h54M23 58h54" stroke="currentColor" strokeWidth="1.3" />
                  <circle cx="27" cy="22" r="6" fill="#882699" />
                  <circle cx="77" cy="25" r="5" fill="#c9a45b" />
                  <circle cx="77" cy="25" r="2" fill="white" />
                </svg>
              </div>
              <div className="min-h-[128px] p-4">
                <h3 className="text-sm font-bold">Connected worldwide</h3>
                <p className="mt-2 text-xs leading-[1.55] text-[#766b64]">
                  An international network that helps your money move across borders with ease.
                </p>
              </div>
            </article>
          </div>
        </section>

        <section className="border-b border-[#e8dfd2] bg-[#f7f3ed] px-4 py-16 sm:px-6 sm:py-20">
          <div data-reveal="up" className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Hear from customers across Canada
            </h2>
            {/* <p className="mt-3 text-sm leading-6 text-[#766b64] sm:text-base">
              Customer stories from across the country.
            </p> */}
          </div>
          <div data-reveal="right" className="testimonial-marquee mt-9 w-full" aria-label="Sample testimonials from customers in Canada">
            <div className="testimonial-track">
              {[0, 1].map((copy) => (
                <div
                  className="flex shrink-0 gap-4 pr-4"
                  key={copy}
                  aria-hidden={copy === 1}
                >
                  {TESTIMONIALS.map(({ name, quote, location }) => (
                    <article
                      className="flex w-[280px] shrink-0 flex-col rounded-2xl border border-[#e6ddd2] bg-[#fffdf9] p-5 sm:w-[310px]"
                      key={`${copy}-${location}`}
                    >
                      <span
                        className="font-serif text-3xl font-bold leading-none text-[#c9a45b]"
                        aria-hidden="true"
                      >
                        “
                      </span>
                      <p className="mt-2 flex-1 text-[13px] leading-5 text-[#302521]">
                        {quote}
                      </p>
                      <div className="mt-5 flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f0e5f3] text-xs font-semibold text-[#882699]">
                          {name.charAt(0)}
                        </span>
                        <div>
                          <p className="text-xs font-semibold">{name}</p>
                          <p className="mt-0.5 text-[11px] text-[#857970]">
                            {location}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {/* <p className="mx-auto mt-4 max-w-2xl text-center text-[10px] text-[#857970]">
            Sample testimonials for layout preview. Replace with verified customer feedback before publishing.
          </p> */}
        </section>

        <section id="faqs" className="scroll-mt-20 border-b border-[#e8dfd2] bg-[#f3eee7] px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-2xl">
            <div data-reveal="up" className="text-center">
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Common questions
              </h2>
              <p className="mt-3 text-sm text-[#766b64] sm:text-base">
                Short answers to what people ask most.
              </p>
            </div>

            <div data-reveal="left" className="mt-8 divide-y divide-[#e6ddd2]">
              <details className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold marker:hidden">
                  How much does it cost to send money?
                  <span className="text-lg font-normal text-[#766b64] transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 max-w-xl text-sm leading-6 text-[#766b64]">
                  The transfer fee and total payment are displayed with your
                  transfer details before you continue.
                </p>
              </details>
              <details className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold marker:hidden">
                  Why do I need to verify my identity?
                  <span className="text-lg font-normal text-[#766b64] transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 max-w-xl text-sm leading-6 text-[#766b64]">
                  Identity verification helps us meet requirements that apply
                  to money transfer services.
                </p>
              </details>
              <details className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold marker:hidden">
                  How do I know the exchange rate?
                  <span className="text-lg font-normal text-[#766b64] transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 max-w-xl text-sm leading-6 text-[#766b64]">
                  The rate for your selected destination is shown in the
                  transfer form before you proceed.
                </p>
              </details>
              <details className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold marker:hidden">
                  Who can I contact for help?
                  <span className="text-lg font-normal text-[#766b64] transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 max-w-xl text-sm leading-6 text-[#766b64]">
                  Visit our Contact Us page to get in touch with Adulis Money
                  Services.
                </p>
              </details>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
